/**
 * TranscriptBackfillButton — one-click "Whisper-transcribe every story
 * that has no script yet". Unlocks multi-language dubs, subtitles and
 * translations for legacy multi-part publishes where the creator never
 * pasted a script. Sits next to BackfillDurationsButton in the Studio
 * top bar. Backend runs sequentially in the background; we poll status.
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import { FileAudio, Loader2, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN = "storylo-admin";
const HEADERS = { "X-Admin-Token": ADMIN_TOKEN };

export default function TranscriptBackfillButton() {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [st, setSt] = useState(null);
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const run = async () => {
    if (!window.confirm(
      "Transcribe every story that has no script stored (using its own " +
      "narration audio) so multi-language dubs and translations work on it?\n\n" +
      "Safe to run anytime — only touches stories missing a script. " +
      "Long documentaries take a couple of minutes each."
    )) return;
    setState("running");
    setSt(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/transcripts/backfill`, null,
        { headers: HEADERS, timeout: 30 * 1000 },
      );
      setSt(data);
      if (data.status !== "running") {
        setState("done");
        setTimeout(() => setState("idle"), 8000);
        return;
      }
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await axios.get(
            `${API}/admin/transcripts/backfill`,
            { headers: HEADERS, timeout: 20 * 1000 },
          );
          setSt(s);
          if (s.status === "done") {
            stopPoll();
            setState("done");
            setTimeout(() => setState("idle"), 10000);
          }
        } catch (_e) { /* transient poll blip — keep going */ }
      }, 5000);
    } catch (e) {
      console.error("transcript-backfill error:", e);
      setSt({ error: e?.response?.data?.detail || e?.message || "Failed" });
      setState("error");
      setTimeout(() => setState("idle"), 6000);
    }
  };

  const label = () => {
    if (state === "running") {
      if (st?.total) {
        const cur = st.current ? ` · ${String(st.current).slice(0, 22)}` : "";
        return `Transcribing ${Math.min((st.done || 0) + 1, st.total)}/${st.total}${cur}`;
      }
      return "Scanning library…";
    }
    if (state === "done") {
      if ((st?.total || 0) === 0) return "All stories have scripts ✓";
      return `Recovered ${st.ok || 0}/${st.total}${st.failed ? ` · ${st.failed} failed` : ""}`;
    }
    if (state === "error") return st?.error || "Try again";
    return "Generate missing scripts";
  };

  return (
    <button
      onClick={run}
      disabled={state === "running"}
      className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
        state === "done" ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
        : state === "error" ? "border-red-500/50 text-red-300 bg-red-500/10"
        : "border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)]"
      }`}
      data-testid="studio-transcript-backfill"
      title="Whisper-transcribe every story that has no script stored — unlocks dubs, subtitles and translations for old multi-part publishes"
    >
      {state === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : state === "done" ? <Check className="w-3.5 h-3.5" />
        : state === "error" ? <X className="w-3.5 h-3.5" />
        : <FileAudio className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{label()}</span>
    </button>
  );
}
